import axios from 'axios';

const envVarCache: Record<string, string | boolean> = {}; // Cache for resolved values
const pendingRequests: Record<string, Promise<string | boolean>> = {}; // Track ongoing requests
const API_URL: string = process.env.REACT_APP_API_URL || '';

class EnvVarService {
  private static instance: EnvVarService;
  private cache: Record<string, string | boolean> = {};
  private pending: Record<string, Promise<string | boolean>> = {};
  // Keys written directly via setEnvVar/setMultipleEnvVars (e.g. agent selection).
  // A slower, already in-flight /getEnvVar fetch must not clobber these once set.
  private explicitlySet: Set<string> = new Set();
  private constructor() {
    if (EnvVarService.instance) {
      return EnvVarService.instance; // Return existing instance
    }

    EnvVarService.instance = this; // Assign the new instance
    this.cache = envVarCache; // Use the existing cache
    this.pending = pendingRequests; // Use a map for tracking pending requests
  }

  public static getInstance(): EnvVarService {
    if (!EnvVarService.instance) {
      EnvVarService.instance = new EnvVarService();
    }
    return EnvVarService.instance;
  }

  public async getEnvVar(envVar: string): Promise<string | boolean> {
    // If already cached, return immediately
    if (Object.prototype.hasOwnProperty.call(this.cache, envVar)) {
      return this.cache[envVar];
    }

    // If a request for this variable is already pending, wait for its result
    if (Object.prototype.hasOwnProperty.call(this.pending, envVar)) {
      return this.pending[envVar]; // Return the promise
    }

    // Create and store a promise for the API request
    this.pending[envVar] = (async () => {
      try {
        const response = await axios.get(`${API_URL}/getEnvVar?envVar=${encodeURIComponent(envVar)}`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });


        // Store the result in the cache, unless a client-side override (e.g. agent
        // selection) already wrote a value for this key while the fetch was in flight.
        const result = typeof response.data === 'object'
          ? JSON.stringify(response.data)
          : String(response.data);
        if (!this.explicitlySet.has(envVar)) {
          this.cache[envVar] = result === 'true' ? true : result === 'false' ? false : result;
        }
        return this.cache[envVar];
      }
      finally {
        // Remove from pending once resolved
        delete this.pending[envVar];
      }
    })();

    return this.pending[envVar];
  }

  public async setEnvVar(envVar: string, value: string | boolean): Promise<string | boolean> {
    // Handle both string and boolean values
    if (typeof value === 'boolean') {
      this.cache[envVar] = value;
    } else {
      this.cache[envVar] = value === 'true' ? true : value === 'false' ? false : value;
    }
    this.explicitlySet.add(envVar);
    return true;
  }

  public async setMultipleEnvVars(updates: Record<string, string | boolean>): Promise<boolean> {
    // Batch update multiple environment variables for better performance
    Object.entries(updates).forEach(([key, value]) => {
      this.explicitlySet.add(key);
      if (typeof value === 'boolean') {
        this.cache[key] = value;
      } else {
        this.cache[key] = value === 'true' ? true : value === 'false' ? false : value;
      }
    });
    return true;
  }
}



// Export a singleton instance of the EnvVarService
export const envVarService: EnvVarService = EnvVarService.getInstance();
